<template>
  <div
    v-if="visibleTooltip"
    ref="whiteTooltipEl"
    class="tooltip"
    :class="[{'active': activeTooltip}, sideClass]"
    @click="$emit('click')"
  >
    <div class="tooltip-header">
      <h3>{{ headerText }}</h3>
      <div class="close-icon" @click.stop="closeTooltip">
        <GreyCloseIcon20 />
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import GreyCloseIcon20 from '@/components/icons/GreyCloseIcon20.vue';

export default {
  name: 'WhiteTooltip',
  components: {GreyCloseIcon20},
  emits: ['closed'],
  props: {
    targetRef: {
      type: [HTMLElement, Object],
      required: true,
    },
    side: {
      type: String,
      default: 'top',
    },
    headerText: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      visibleTooltip: false,
      activeTooltip: false,
      targetElement: null,
      resizeHandler: null,
      scrollHandler: null,
    };
  },
  computed: {
    sideClass() {
      return `${this.side}`;
    },
  },
  beforeDestroy() {
    this.removeResizeListener();
    this.removeScrollListener();
  },
  methods: {
    positionTooltip() {
      try {
        const tooltip = this.$refs.whiteTooltipEl;
        if (!tooltip || !this.targetElement) return;

        const targetElementRect = this.targetElement.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate initial position
        let leftPos = targetElementRect.left - 24;
        let topPos;

        switch (this.sideClass) {
          case 'bottom':
            topPos = targetElementRect.bottom + 6;
            break;
          case 'top':
          default:
            topPos = targetElementRect.top - tooltipRect.height - 6;
        }

        // Check if tooltip would go outside the right edge of the screen
        if (leftPos + tooltipRect.width > viewportWidth) {
          leftPos = viewportWidth - tooltipRect.width - 10; // 10px padding from edge
        }

        // Check if tooltip would go outside the left edge of the screen
        if (leftPos < 10) {
          leftPos = 10; // 10px padding from edge
        }

        // Check if tooltip would go outside the bottom of the screen
        if (topPos + tooltipRect.height > viewportHeight && this.sideClass === 'bottom') {
          // If it's positioned at the bottom but would go outside, flip to top
          topPos = targetElementRect.top - tooltipRect.height - 6;
        }

        // Check if tooltip would go outside the top of the screen
        if (topPos < 10 && this.sideClass === 'top') {
          // If it's positioned at the top but would go outside, flip to bottom
          topPos = targetElementRect.bottom + 6;
        }

        tooltip.style.left = `${leftPos}px`;
        tooltip.style.top = `${topPos}px`;
      } catch (error) {
        console.error('Error positioning tooltip:', error);
        this.hideTooltip();
      }
    },

    addResizeListener() {
      if (this.resizeHandler) return;

      this.resizeHandler = () => {
        if (this.visibleTooltip) {
          this.positionTooltip();
        }
      };

      window.addEventListener('resize', this.resizeHandler);
    },

    removeResizeListener() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
    },

    addScrollListener() {
      if (this.scrollHandler) return;

      this.scrollHandler = () => {
        if (this.visibleTooltip) {
          this.positionTooltip();
        }
      };

      window.addEventListener('scroll', this.scrollHandler, true); // Capture phase to catch all scroll events
    },

    removeScrollListener() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler, true);
        this.scrollHandler = null;
      }
    },

    showTooltip() {
      if (!this.targetElement) {
        this.targetElement = this.targetRef instanceof HTMLElement ? this.targetRef :
          (this.targetRef && this.targetRef.$el) ? this.targetRef.$el :
            (this.targetRef && typeof this.targetRef === 'object') ? this.targetRef : null;

        if (!this.targetElement) {
          return;
        }
      }

      this.visibleTooltip = true;

      setTimeout(() => {
        this.positionTooltip();
        this.addResizeListener();
        this.addScrollListener();
        this.activeTooltip = true;
      }, 50);
    },
    hideTooltip() {
      this.activeTooltip = false;
      this.visibleTooltip = false;
      this.removeResizeListener();
      this.removeScrollListener();
      this.$emit('closed');
    },
    closeTooltip() {
      this.hideTooltip();
    },
  },
};
</script>

<style scoped>
.tooltip {
  position: fixed;
  visibility: hidden;
  background-color: var(--white-color);
  box-shadow: -2px 4px 10px 0px #4D6A9326;
  padding: 10px 16px;
  z-index: 9999;
  max-width: 412px;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
}

.tooltip::before {
  all: unset;
}

.tooltip span {
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  vertical-align: middle;
  color: var(--secondary-black-color);
  white-space: break-spaces;
}

.active {
  visibility: visible;
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.tooltip-header h3 {
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  text-align: left;
  color: var(--accent-color);
}

.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  z-index: 1;
}
</style>
